import React, { useEffect, useState, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Polyline, Popup } from "react-leaflet";
import { LeafletTrackingMarker } from "react-leaflet-tracking-marker";
import { toast } from "react-toastify";
import "leaflet/dist/leaflet.css";
import "react-toastify/dist/ReactToastify.css";
import L from "leaflet";
import "../styles/TripPlayer.css";
import { fetchTripHistory } from "../utils/flespiTripApi";

const TripPlayer = ({ deviceId, from, to }) => {
  const [tripData, setTripData] = useState([]);
  const [playbackIndex, setPlaybackIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000); // 1s default
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [days, setDays] = useState([]);
  const tableRef = useRef(null);
  const intervalRef = useRef(null);

  const defaultCenter = [6.5244, 3.3792]; // Lagos, Nigeria

  // Filter valid coordinates and required fields
  const validTripData = useMemo(() => {
    return tripData.filter(
      (p) =>
        typeof p["position.latitude"] === "number" &&
        typeof p["position.longitude"] === "number" &&
        typeof p["position.speed"] === "number" &&
        !isNaN(p["position.latitude"]) &&
        !isNaN(p["position.longitude"]) &&
        !isNaN(p["position.speed"])
    );
  }, [tripData]);

  // Group validTripData by day
  const tripsByDay = useMemo(() => {
    return validTripData.reduce((acc, point) => {
      const date = new Date(point.timestamp * 1000).toISOString().split("T")[0];
      if (!acc[date]) acc[date] = [];
      acc[date].push(point);
      return acc;
    }, {});
  }, [validTripData]);

  // Update days list
  useEffect(() => {
    const dayKeys = Object.keys(tripsByDay).sort();
    setDays(dayKeys);
    if (dayKeys.length > 0 && !selectedDay) {
      setSelectedDay(dayKeys[0]);
    }
  }, [tripsByDay, selectedDay]);

  // Memoize currentDayData
  const currentDayData = useMemo(() => {
    return selectedDay ? tripsByDay[selectedDay] || [] : [];
  }, [selectedDay, tripsByDay]);

  const current = currentDayData[playbackIndex] || null;
  const center = current
    ? [current["position.latitude"], current["position.longitude"]]
    : defaultCenter;

  const markerIcon = new L.Icon({
    iconUrl: require("../assets/icons/car.png"),
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });

  useEffect(() => {
    const loadTrip = async () => {
      if (!deviceId || !from || !to) {
        setError("Invalid trip parameters.");
        setLoading(false);
        return;
      }

      try {
        const data = await fetchTripHistory(deviceId, from, to);
        console.log("Trip data received:", data);
        if (!data.length) {
          setError("No trip data found for the selected period.");
        } else {
          setTripData(data);
        }
      } catch (err) {
        console.error("Error loading trip:", err);
        setError("Failed to load trip history. Please check the date range or try again.");
      } finally {
        setLoading(false);
      }
    };
    loadTrip();
  }, [deviceId, from, to]);

  useEffect(() => {
    if (playing && currentDayData.length > 0) {
      intervalRef.current = setInterval(() => {
        setPlaybackIndex((prev) =>
          prev < currentDayData.length - 1 ? prev + 1 : prev
        );
      }, speed);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [playing, speed, currentDayData.length]);

  // Scroll to the current row
  useEffect(() => {
    if (tableRef.current && currentDayData.length > 0) {
      const row = tableRef.current.querySelector(`tr[data-index="${playbackIndex}"]`);
      if (row) {
        row.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [playbackIndex]);

  const handleReset = () => {
    setPlaybackIndex(0);
    setPlaying(false);
  };

  const handleDayChange = (day) => {
    setSelectedDay(day);
    setPlaybackIndex(0);
    setPlaying(false);
  };

  const handleCopyLatLng = (lat, lng) => {
    const latLng = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    navigator.clipboard.writeText(latLng).then(() => {
      toast.success("Lat/Lng copied to clipboard!");
    }).catch((err) => {
      console.error("Failed to copy:", err);
      toast.error("Failed to copy.");
    });
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${date.getFullYear()} ${date
      .getHours()
      .toString()
      .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}:${date
      .getSeconds()
      .toString()
      .padStart(2, "0")}`;
  };

  if (loading) return <div style={{ padding: "20px" }}>⏳ Loading trip...</div>;
  if (error) return <div style={{ padding: "20px", color: "red" }}>❌ {error}</div>;
  if (!validTripData.length) return <div style={{ padding: "20px" }}>No valid trip data available.</div>;

  return (
    <div className="trip-map-container">
      {/* Day Toggle */}
      <div className="day-toggle">
        <label>Select Day: </label>
        <select
          value={selectedDay || ""}
          onChange={(e) => handleDayChange(e.target.value)}
        >
          {days.map((day) => (
            <option key={day} value={day}>
              {day}
            </option>
          ))}
        </select>
      </div>

      {/* Map */}
      <MapContainer
        center={center}
        zoom={13}
        className="trip-map"
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Polyline
          positions={currentDayData.map((p) => [
            p["position.latitude"],
            p["position.longitude"],
          ])}
          color="blue"
        />
        {current && (
          <LeafletTrackingMarker
            position={[current["position.latitude"], current["position.longitude"]]}
            previousPosition={[current["position.latitude"], current["position.longitude"]]}
            icon={markerIcon}
            rotationAngle={current["position.direction"] || 0}
            duration={speed}
          >
            <Popup>
              Speed: {current["position.speed"]} km/h<br />
              Status: {current["movement.status"] || "Unknown"}<br />
              Time: {formatTimestamp(current.timestamp)}
            </Popup>
          </LeafletTrackingMarker>
        )}
      </MapContainer>

      {/* Playback Controls */}
      <div className="trip-controls">
        <button onClick={() => setPlaying(!playing)}>
          {playing ? "Pause ⏸" : "Play ▶"}
        </button>
        <button onClick={handleReset}>Restart 🔁</button>
        <select onChange={(e) => setSpeed(Number(e.target.value))} value={speed}>
          <option value="2000">🐢 Slow (2s)</option>
          <option value="1000">🚗 Normal (1s)</option>
          <option value="500">🏎 Fast (0.5s)</option>
        </select>
        {currentDayData.length > 0 && (
          <input
            type="range"
            min="0"
            max={currentDayData.length - 1}
            value={playbackIndex}
            onChange={(e) => {
              setPlaybackIndex(Number(e.target.value));
              setPlaying(false);
            }}
            style={{ width: "200px" }}
          />
        )}
      </div>

      {/* Data Table */}
      <div className="trip-data-table">
        <table ref={tableRef}>
          <thead>
            <tr>
              <th>timestamp</th>
              <th>server_timestamp</th>
              <th>ident</th>
              <th>position.latitude</th>
              <th>position.longitude</th>
              <th>position.altitude</th>
              <th>position.direction</th>
              <th>position.speed</th>
              <th>action</th>
            </tr>
          </thead>
          <tbody>
            {currentDayData.map((point, index) => (
              <tr
                key={index}
                data-index={index}
                className={index === playbackIndex ? "selected-row" : ""}
                onClick={() => {
                  setPlaybackIndex(index);
                  setPlaying(false);
                }}
              >
                <td>{formatTimestamp(point.timestamp)}</td>
                <td>{point.server_timestamp ? formatTimestamp(point.server_timestamp) : "N/A"}</td>
                <td>{point.ident || "N/A"}</td>
                <td>{point["position.latitude"].toFixed(6)}</td>
                <td>{point["position.longitude"].toFixed(6)}</td>
                <td>{point["position.altitude"] || 0} m</td>
                <td>{point["position.direction"] || 0}°</td>
                <td>{point["position.speed"]} km/h</td>
                <td>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyLatLng(point["position.latitude"], point["position.longitude"]);
                    }}
                  >
                    Copy Lat/Lng
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TripPlayer;